/* useTodaySignal.js — Ambient operational signal for the Today
 * bottom-nav tab. Returns a single boolean: is there *anything*
 * operationally important waiting for the agent today?
 *
 * Doctrine:
 *   • NOT a notification system. NOT unread counters. NOT alerts.
 *     A calm presence/absence indicator only.
 *   • Reuses the existing /api/today/sheet response — same source
 *     of truth as the Today page. No parallel state.
 *   • Module-level cache (60s TTL) so multiple mounts and the
 *     bottom-nav share one network call.
 *   • Custom event `cf:today:signal:bump` lets the Today page tell
 *     the indicator to refresh the moment a blocker resolves or a
 *     follow-up is snoozed — no waiting for the next poll.
 *
 * Exposes:
 *   useTodaySignal() → { hasPending, refresh, lastUpdated }
 *   bumpTodaySignal() — call this after any local mutation that
 *     might flip the pending state (resolve, snooze, chase).
 */
import { useCallback, useEffect, useState } from "react";
import { api } from "../lib/api";

const REFRESH_MS = 60_000;
const BUMP_EVENT = "cf:today:signal:bump";
const PULSE_KEY  = "cf_today_pulse_day";

let _cached = null;        // { hasPending: bool, at: number }
let _inflight = null;

async function _fetchOnce(force = false) {
  const now = Date.now();
  if (!force && _cached && now - _cached.at < REFRESH_MS) return _cached;
  if (_inflight) return _inflight;
  _inflight = (async () => {
    try {
      const res = await api.get("/api/today/sheet");
      const data = res?.data || {};
      const stats = data.quick_stats || {};
      const needs   = Number(stats.needs_attention_count ?? data.needs_attention?.length ?? 0);
      // iter-alignment · Pre-pilot coherence (2026-02).  Command
      // Centre KPI prefers the UNIQUE affected-deals count so a
      // single deal carrying multiple blockers doesn't inflate
      // the tile.  Falls back to the ITEM count for older
      // backends that haven't shipped the split yet.
      const needsDealCount = Number(
        stats.needs_attention_deal_count
        ?? stats.needs_attention_count
        ?? data.needs_attention?.length
        ?? 0
      );
      const followUps = Number(stats.follow_ups_due ?? data.follow_ups_due?.length ?? 0);
      _cached = {
        hasPending: (needs + followUps) > 0,
        pendingCount: needs + followUps,
        at: Date.now(),
      };
      return _cached;
    } catch {
      _cached = _cached || { hasPending: false, pendingCount: 0, at: Date.now() };
      return _cached;
    } finally {
      _inflight = null;
    }
  })();
  return _inflight;
}

export function bumpTodaySignal() {
  // Invalidate cache + tell every subscriber to refresh.
  _cached = null;
  if (typeof window !== "undefined") {
    window.dispatchEvent(new CustomEvent(BUMP_EVENT));
  }
}

export function useTodaySignal(enabled = true) {
  const [hasPending, setHasPending] = useState(_cached?.hasPending ?? false);
  const [pendingCount, setPendingCount] = useState(_cached?.pendingCount ?? 0);
  const [shouldPulse, setShouldPulse] = useState(false);

  const refresh = useCallback(async (force = false) => {
    const c = await _fetchOnce(force);
    setHasPending(c.hasPending);
    setPendingCount(c.pendingCount ?? 0);
  }, []);

  useEffect(() => {
    if (!enabled) return;
    let alive = true;
    refresh(false);
    const t = setInterval(() => alive && refresh(false), REFRESH_MS);
    const onFocus = () => alive && refresh(true);
    const onBump  = () => alive && refresh(true);
    window.addEventListener("focus", onFocus);
    window.addEventListener(BUMP_EVENT, onBump);
    return () => {
      alive = false;
      clearInterval(t);
      window.removeEventListener("focus", onFocus);
      window.removeEventListener(BUMP_EVENT, onBump);
    };
  }, [refresh,enabled]);

  // Morning pulse — fires exactly once per calendar day, the first
  // time the dot surfaces with pending items. Hard-keyed by
  // localStorage so the next page navigation / hot reload / app
  // mount never re-triggers it that day. Disappears silently if
  // the workspace is calm.
  useEffect(() => {
    if (!hasPending || shouldPulse) return;
    try {
      const today = new Date().toISOString().slice(0, 10); // YYYY-MM-DD
      const last = localStorage.getItem(PULSE_KEY);
      if (last === today) return;
      localStorage.setItem(PULSE_KEY, today);
      setShouldPulse(true);
      // Match the keyframe duration; lift the attribute after the
      // animation ends so the dot becomes fully static. Slightly
      // longer than 800ms to ensure the run finishes cleanly.
      const timer = setTimeout(() => setShouldPulse(false), 900);
      return () => clearTimeout(timer);
    } catch {
      // localStorage blocked (private mode / disabled). Stay calm:
      // skip the pulse. The dot itself still renders.
    }
  }, [hasPending, shouldPulse]);

  return { hasPending, pendingCount, shouldPulse, refresh };
}
